/**
 * 入札後ポジション chart (= {@link ../PositionChart}) の Y 軸目盛り算出。
 *
 * recharts 既定 (`domain=[0,'auto']` + `interval='preserveEnd'`) に任せると 2 点困る
 * (issue #5209、 ドッグフーディング 2026-07-31。 headless 実測は下記):
 *   - 0 が目盛りに乗るのは nice tick の副作用でしかなく保証が無い。 乗っても他の dashed grid と
 *     同じ見た目で、 X 軸線は domain 下端に描かれるため、 負値 (= 逆方向入札 / 余剰コマの売り札)
 *     があると「どこが 0 か」 を目視で特定できない。
 *   - `interval='preserveEnd'` が目盛りを間引き、 間隔が不均等になる
 *     (実測: need 400 のみのコマで目盛りが `[0, 100, 200, 400]` = 上端 1 区間だけ幅 2 倍。
 *      spec L314 の「常に均等な間隔」 に反する)。
 * そこで 0 を必ず目盛りに乗せた ticks / domain を自前で決め、 `interval=0` で間引きも止める。
 *
 * spec `docs/specs/bidding/spot-bidding.md` AC-BSP-016 (L314) の 2 条件に従う:
 *   1. 「Y軸の原点(0)の位置は、 不足分・超過分の有無に応じて上下に移動してよい (固定しない)」
 *      → 0 の上下で目盛りの「本数」を変える (= 非対称)。 design proto (`bidding/spot.html:3931`)
 *        は domain を ±対称にしていて 0 が常に縦中央に固定される。 実測データ −28〜+67 に対し
 *        domain −100〜+100 = 描画高の 47% しか使わないため、 dev には非対称版で移植する
 *        [proto差異: 対称 domain を非対称化。 spec L314「固定しない」準拠]。
 *   2. 「目盛りの刻み幅は表示データ全体の範囲から算出し、 常に均等な間隔で表示する」
 *      → 刻み幅 (step) は 0 の上下で共通の 1 種類だけ。 片側だけ間隔を変えることはしない。
 *
 * design proto から引き継ぐ性質:
 *   - 「きりのよい」刻み (1/1.5/2/2.5/5 × 10^n) だけを使う (`spot.html:3905` `_niceStep`)。
 *     proto の候補は 1/2/5/10 だが、 1.5 系 (15 MW 等) / 2.5 系 (25 MW 等) を足すと
 *     非対称配分時の余白が縮む。 実測 (headless、 プロット高 120px に対する系列の占有率):
 *       −28〜+67 … ±対称 (proto) 47% → 本実装 76%
 *       −13〜+41 … 候補 1/2/2.5/5 では 67.5% (刻み 20) → 1.5 を足すと 90% (刻み 15)
 *     刻み幅の算出方法自体は spec L314 で「EN 判断範囲」 とされている。
 *   - 凡例で非表示にした系列は範囲計算から除く (呼び出し側が values を絞る)。
 */

/** 刻み幅の候補係数 (= この値 × 10^n だけを刻み幅として使う)。 昇順。 */
const NICE_FRACTIONS = [1, 1.5, 2, 2.5, 5] as const

/**
 * 目盛り間隔の総数の上限 (= 目盛り線は最大 6 本)。
 * chart 高は既定 160px (プロット領域は X 軸 / margin を除いて実測 120px) なので、
 * 6 本 = 24px 間隔が font 12px のラベルを詰まらせない下限。 proto は常に 5 本
 * (`spot.html:3924` `_SEG=2`) だが、 非対称配分では 6 本目を許すと余白が縮む
 * (−28〜+67 で 5 本 63% → 6 本 76%)。
 */
const MAX_SEGMENTS = 5

/** 全系列 0 (= 入札 0 件 かつ need 0) のときの Y 軸上端 MW。 0 だけの軸は描けないため。 */
const EMPTY_MAX_MW = 4

/** 浮動小数の丸め誤差で ceil が 1 段跳ねるのを防ぐ許容値。 */
const EPSILON = 1e-9

export type PositionYAxis = {
  /** recharts `<YAxis domain>` (= [下端, 上端]。 いずれも刻み幅の整数倍)。 */
  domain: [number, number]
  /** recharts `<YAxis ticks>`。 0 を必ず含む昇順の目盛り値。 */
  ticks: number[]
  /** 負値を含むか (= 0 基準線を実線で引くか)。 */
  hasNegative: boolean
}

/** 浮動小数の桁あふれ (0.1 × 3 = 0.30000000000000004) を落として目盛りラベルを綺麗に保つ。 */
function roundTick(value: number): number {
  return Number(value.toPrecision(12))
}

/** `rough` 以上で最小の「きりのよい」刻み幅 (= 1/2/2.5/5 × 10^n)。 */
export function niceStep(rough: number): number {
  if (!Number.isFinite(rough) || rough <= 0) return 1
  const exp = 10 ** Math.floor(Math.log10(rough))
  const fraction = rough / exp
  const nice = NICE_FRACTIONS.find((c) => fraction <= c + EPSILON)
  return roundTick((nice ?? 10) * exp)
}

/** `step` (= きりのよい刻み幅) の 1 段上の刻み幅。 */
function nextNiceStep(step: number): number {
  const exp = 10 ** Math.floor(Math.log10(step) + EPSILON)
  const fraction = step / exp
  const nice = NICE_FRACTIONS.find((c) => c > fraction + EPSILON)
  return roundTick((nice ?? 10) * exp)
}

/**
 * 表示中の系列値から Y 軸の domain / ticks を決める。
 *
 * 手順:
 *   1. 0 を必ず含む範囲 (= [min(0, 最小値), max(0, 最大値)]) を取る。
 *   2. 目盛り線が {@link MAX_SEGMENTS} + 1 本以内に収まる最小の「きりのよい」刻み幅を選ぶ。
 *      最小の刻み幅 = 余白が最小 = データが描画高を最も広く使う。
 *   3. 0 から上下それぞれ `ceil(必要幅 / 刻み幅)` 段ずつ伸ばす (= 上下で段数が異なる = 非対称)。
 *
 * @param values 表示中の系列の実値 (need / cover / executed)。 非表示系列は含めない。
 */
export function computePositionYAxis(values: readonly number[]): PositionYAxis {
  let max = 0
  let min = 0
  for (const v of values) {
    if (!Number.isFinite(v)) continue
    if (v > max) max = v
    if (v < min) min = v
  }
  const negativeMagnitude = -min

  // 全系列 0: 0 幅の軸は描けないので固定レンジ (0〜4 MW) を返す。
  if (max === 0 && negativeMagnitude === 0) {
    return {
      domain: [0, EMPTY_MAX_MW],
      ticks: [0, 1, 2, 3, 4],
      hasNegative: false,
    }
  }

  // 全系列が負 (= max が 0 ちょうど) だと 0 が domain 上端になり、 0 基準線
  // (`PositionChart` の `<ReferenceLine y={0}>`) がプロット領域の最上辺と重なって
  // 線幅の半分が切れて見える (デザイナー目視 2026-08-17、 review #5295 で devin が
  // 事前指摘していた事象)。 上に 1 段だけ余白を確保して 0 を上端から下げる。
  // 逆 (全系列が正) は 0 が下端に来るが、 そこは X 軸線そのものなので切れない
  // (hasNegative=false = 0 基準線を引かない) ため対称の処理は要らない。
  const needsZeroHeadroom = min < 0 && max === 0

  let step = niceStep((max + negativeMagnitude) / MAX_SEGMENTS)
  let positiveSegments = 0
  let negativeSegments = 0
  // step は 1 段ごとに 25% 以上増えるので必ず収束する (guard は無限ループ保険)。
  for (let guard = 0; guard < 32; guard += 1) {
    positiveSegments = Math.max(
      Math.ceil(max / step - EPSILON),
      needsZeroHeadroom ? 1 : 0,
    )
    negativeSegments = Math.ceil(negativeMagnitude / step - EPSILON)
    if (positiveSegments + negativeSegments <= MAX_SEGMENTS) break
    step = nextNiceStep(step)
  }

  const ticks: number[] = []
  for (let i = -negativeSegments; i <= positiveSegments; i += 1) {
    ticks.push(roundTick(i * step))
  }
  return {
    domain: [ticks[0], ticks[ticks.length - 1]],
    ticks,
    hasNegative: min < 0,
  }
}

/**
 * Y 軸範囲の計算対象になる値 (= 表示中の系列だけ) を集める。
 * 凡例で非表示にした系列を範囲に残すと「隠したのに軸が縮まない」 ため除く
 * (design proto `spot.html:3915` の `if (!ev[k]) return` と同じ扱い)。
 *
 * `visible.bar` は市場ごとの単一 bar 系列 (spot=executed / 時間前市場=remain、
 * {@link PositionChartColors} の `bar`) で、 どちらの値を集めるかは `barKey` で決める。
 * remain が undefined のコマ (need 取得不可) は描かれないので範囲計算に含めない。
 *
 * スポット (AC-BSP-016) の通常/ブロック分割描画は約定 bar を 通常 / ブロック の積み上げ
 * (同一 stackId) で持つため、 個別値ではなく符号ごとの合計を範囲に含める (design
 * `spot.html:4190` `_yAxisRange` と同一算法、 PR #5295 のコメント通り dev/proto で揃える)。
 * 凡例に出ない系列は呼び出し側 (`resolveEffectiveVisible`) が false にして渡す
 * (= 分割描画なら `bar` が、 単一 bar なら `executedNormal`/`executedBlock` が常に false)。
 */
export function collectVisibleYValues(
  data: ReadonlyArray<{
    need: number
    cover: number
    coverSubmitted: number
    executed: number
    remain: number | undefined
    executedNormal?: number
    executedBlock?: number
    unknownIssuer?: number
  }>,
  visible: {
    bar: boolean
    executedNormal: boolean
    executedBlock: boolean
    need: boolean
    cover: boolean
    coverSubmitted: boolean
    unknownIssuer?: boolean
  },
  barKey: 'executed' | 'remain',
): number[] {
  const values: number[] = []
  const isStackedExecuted = visible.executedNormal || visible.executedBlock
  for (const d of data) {
    if (isStackedExecuted) {
      // 積み上げは符号ごとに合算する (負値の bar は下方向に積むため)。
      let pos = 0
      let neg = 0
      if (visible.executedNormal) {
        const v = d.executedNormal ?? 0
        if (v > 0) pos += v
        else neg += v
      }
      if (visible.executedBlock) {
        const v = d.executedBlock ?? 0
        if (v > 0) pos += v
        else neg += v
      }
      values.push(pos, neg)
    } else if (visible.bar) {
      const v = barKey === 'remain' ? d.remain : d.executed
      if (v !== undefined) values.push(v)
    }
    if (visible.need) values.push(d.need)
    if (visible.cover) values.push(d.cover)
    if (visible.coverSubmitted) values.push(d.coverSubmitted)
    if (visible.unknownIssuer === true && d.unknownIssuer !== undefined) {
      values.push(d.unknownIssuer)
    }
  }
  return values
}
